import SaveIcon from "@mui/icons-material/Save";
import { IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { Typography } from "@platform/design-system";

type SaveStatus = "idle" | "pending" | "saving";

interface Props {
  status: SaveStatus;
  lastUpdated?: Date;
  onSave: VoidFunction;
}

const SAVE_STATUS: Record<string, SaveStatus> = {
  IDLE: "idle",
  PENDING: "pending",
  SAVING: "saving",
};

const getTimeSinceUpdate = (lastUpdated: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - lastUpdated.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 30));
  const diffInYears = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 365));

  if (diffInMinutes < 1) {
    return "just now";
  }
  if (diffInMinutes < 2) {
    return "1 minute ago";
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`;
  }
  if (diffInHours < 2) {
    return "1 hour ago";
  }
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  }
  if (diffInDays < 2) {
    return "1 day ago";
  }
  if (diffInDays < 30) {
    return `${diffInDays} days ago`;
  }
  if (diffInMonths < 2) {
    return "1 month ago";
  }
  if (diffInMonths < 12) {
    return `${diffInMonths} months ago`;
  }
  if (diffInYears < 2) {
    return "1 year ago";
  }
  return `${diffInYears} years ago`;
};

const getLabel = (status: SaveStatus, lastUpdated?: Date): string => {
  if (status === SAVE_STATUS.SAVING) {
    return "saving draft";
  }
  if (lastUpdated) {
    return `Draft Last saved ${getTimeSinceUpdate(lastUpdated)}`;
  }
  return "Draft";
};

const SaveDraftStatus = ({
  status,
  lastUpdated,
  onSave,
}: Props): React.JSX.Element => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!lastUpdated) return;

    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastUpdated]);

  if (status === SAVE_STATUS.IDLE && !lastUpdated) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={onSave}
        size="small"
        color="brandPrimary"
        aria-label="save draft"
        disabled={status !== SAVE_STATUS.PENDING}
        loading={status === SAVE_STATUS.SAVING}
      >
        <SaveIcon />
      </IconButton>
      <Typography variant="body-medium" color="textSecondary">
        {getLabel(status, lastUpdated)}
      </Typography>
    </>
  );
};

export { SAVE_STATUS, SaveDraftStatus, SaveStatus };
